import { createContext, useContext, useMemo, useReducer } from "react";

const STORAGE_KEY = "setup_wizard_state";

const initialState = {
  clientId: "",
  redirectUri: "",
  company: null,
  companyId: null,
  roles: [],
  userId: null,
  username: "",
};

function loadState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? { ...initialState, ...JSON.parse(raw) } : initialState;
  } catch {
    return initialState;
  }
}

function saveState(state) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function reducer(state, action) {
  let next;
  switch (action.type) {
    case "SET_CLIENT_INFO":
      next = { ...state, clientId: action.payload.clientId, redirectUri: action.payload.redirectUri };
      break;
    case "SET_COMPANY":
      next = {
        ...state,
        company: action.payload,
        companyId: action.payload?.id ?? state.companyId,
      };
      break;
    case "ADD_ROLE":
      next = { ...state, roles: [...state.roles, action.payload] };
      break;
    case "SET_USER":
      next = { ...state, userId: action.payload.userId, username: action.payload.username };
      break;
    case "RESET":
      next = initialState;
      break;
    default:
      return state;
  }
  saveState(next);
  return next;
}

const SetupStateContext = createContext(null);
const SetupDispatchContext = createContext(null);

export function SetupProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, loadState);
  const dispatchValue = useMemo(() => dispatch, []);

  return (
    <SetupStateContext.Provider value={state}>
      <SetupDispatchContext.Provider value={dispatchValue}>
        {children}
      </SetupDispatchContext.Provider>
    </SetupStateContext.Provider>
  );
}

export function useSetupState() {
  const ctx = useContext(SetupStateContext);
  if (!ctx) throw new Error("useSetupState must be used within a SetupProvider");
  return ctx;
}

export function useSetupDispatch() {
  const ctx = useContext(SetupDispatchContext);
  if (!ctx) throw new Error("useSetupDispatch must be used within a SetupProvider");
  return ctx;
}

export const setClientInfo = (clientId, redirectUri) => ({
  type: "SET_CLIENT_INFO",
  payload: { clientId, redirectUri },
});
export const setCompany = (company) => ({ type: "SET_COMPANY", payload: company });
export const addRole = (role) => ({ type: "ADD_ROLE", payload: role });
export const setUser = (userId, username) => ({ type: "SET_USER", payload: { userId, username } });
export const resetSetup = () => ({ type: "RESET" });

// Clears persisted wizard state directly in sessionStorage, without going
// through dispatch/useReducer. Use this right before leaving the SPA
// entirely (e.g. a window.location redirect) - dispatching resetSetup()
// there instead re-renders the still-mounted setup page with empty context
// fields, which trips its guard (and the guards of pages it redirects to)
// and flashes through earlier wizard steps before the real navigation away
// takes effect.
export function clearPersistedSetupState() {
  sessionStorage.removeItem(STORAGE_KEY);
}
